import { useState, useEffect, useCallback } from "react"
import ListPageHeader from '../common/ListPageHeader.js';
import { useNavigate } from "react-router-dom"
import { onContractService } from "../../services/onContractService"
import reportService from "../../services/ReportService"
import { useAuth } from "../../context/AuthContext"
import { DashboardLayout } from '../common/page_component/index.js'
import Loader from "../common/Loader.js"
import Alert from "../common/Alert"
import { Plus, Download, TrendingUp, TrendingDown } from "lucide-react"
import ListFilterPanel from '../common/ListFilterPanel.js'
import Button from '../common/Button.js'
import RowActionsMenu from '../common/RowActionsMenu.js'
import { useTranslation } from 'react-i18next';
import { listIdLinkClass, listIdLinkMobileClass } from '../common/uiClasses.js';
import usePaginatedListFetch from '../../utils/usePaginatedListFetch.js'
import { mergeLoadedPage } from '../../utils/mergeLoadedPage.js'
import ListPagination from '../common/ListPagination.js'


const PAGE_SIZE = 10
const EMPTY_AGGREGATE = {
  contracts: 0,
  shipments: 0,
  freight: 0,
  contract_value: 0,
  paid_amount: 0,
  balance_amount: 0,
  trip_pl: 0,
}

const OnContractList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate()
  const { user } = useAuth()
  const [contracts, setContracts] = useState([])
  const [vehicles, setVehicles] = useState([])
  const [loading, setLoading] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [aggregate, setAggregate] = useState(EMPTY_AGGREGATE)
  const [exportingAll, setExportingAll] = useState(false)
  const [exportingId, setExportingId] = useState(null)
  const [openMenuId, setOpenMenuId] = useState(null)
  const [filters, setFilters] = useState({ vehicle_id: "" })

  useEffect(() => {
    fetchVehicles()
  }, [])

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (openMenuId && !e.target.closest(".menu-container")) {
        setOpenMenuId(null)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [openMenuId])

  const fetchVehicles = async () => {
    try {
      const res = await onContractService.getAllVehicles()
      const data = res.data.vehicles || res.data.data || res.data || []
      setVehicles(Array.isArray(data) ? data : [])
    } catch (err) {
      console.error("Error fetching vehicles:", err)
    }
  }

  const fetchContracts = useCallback(async () => {
    try {
      setLoading(true)
      const cleanFilters = Object.fromEntries(Object.entries(filters).filter(([_, v]) => v !== ""))
      const res = await onContractService.getAll({
        ...cleanFilters,
        page: currentPage,
        per_page: PAGE_SIZE,
      })
      const data = res.data.contracts || res.data.data || []
      const rows = Array.isArray(data) ? data : []
      const pagination = res.data.pagination || {}
      setAggregate(res.data.aggregate || EMPTY_AGGREGATE)
      setContracts((prev) => mergeLoadedPage(currentPage, rows, prev))
      setTotalPages(pagination.pages || 1)
    } catch (err) {
      console.error("Error fetching contracts:", err)
      Alert.error(err.response?.data?.error || "Failed to load contracts")
      if (currentPage <= 1) {
        setContracts([])
        setAggregate(EMPTY_AGGREGATE)
      }
    } finally {
      setLoading(false)
    }
  }, [filters, currentPage])

  usePaginatedListFetch({
    fetchPage: fetchContracts,
    setCurrentPage,
    currentPage,
    resetPageDeps: [filters.vehicle_id],
  })

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value })
  }

  const handleClearFilters = () => {
    setFilters({ vehicle_id: "" })
  }

  const hasActiveFilters = !!filters.vehicle_id

  const handleDelete = async (contractId) => {
    if (!window.confirm("Are you sure you want to delete this contract? This action cannot be undone.")) return
    try {
      await onContractService.delete(contractId)
      Alert.success("Contract deleted successfully")
      if (currentPage > 1) setCurrentPage(1)
      else fetchContracts()
    } catch (err) {
      Alert.error(err.response?.data?.error || "Failed to delete contract")
    }
    setOpenMenuId(null)
  }

  const handleExport = async (contractId = null) => {
    try {
      if (contractId) {
        setExportingId(contractId)
      } else {
        setExportingAll(true)
      }
      const params = contractId ? { contract_id: contractId } : {}
      await reportService.downloadOnContractReport(params)
      Alert.success("Excel report downloaded")
    } catch (err) {
      Alert.error("Failed to export report")
    } finally {
      setExportingId(null)
      setExportingAll(false)
    }
  }

  const canEdit = () => user?.role === "System Admin" || user?.role === "Admin" || user?.role === "Accountant"
  const canDelete = () => user?.role === "System Admin" || user?.role === "Admin"

  const fmt = (v) =>
    parseFloat(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })

  const PLBadge = ({ value }) => {
    const v = parseFloat(value || 0)
    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-bold ${
          v >= 0 ? "text-secondary" : "text-secondary"
        }`}
      >
        {v >= 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
        ₹{v.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
      </span>
    )
  }

  const BalanceBadge = ({ value }) => {
    const v = parseFloat(value || 0)
    return (
      <span
        className={`inline-block px-2 py-1 rounded text-xs font-bold ${
          v <= 0
            ? "text-secondary"
            : "text-secondary"
        }`}
      >
        {v <= 0 ? "Cleared" : `₹${fmt(v)}`}
      </span>
    )
  }

  return (
    <DashboardLayout>
      {/* Page Header */}
      <ListPageHeader title="On-Contract Records" className="mb-4 sm:mb-6">
        <Button mobileIconOnly
          variant="secondary"
          icon={Download}
          onClick={() => handleExport(null)}
          disabled={exportingAll || aggregate.contracts === 0}
          loading={exportingAll}
        >
          {exportingAll ? 'Exporting...' : 'Export All'}
        </Button>
        {canEdit() && (
          <Button mobileIconOnly
            variant="primary"
            icon={Plus}
            onClick={() => navigate('/on-contracts/add')}
          >
            Add Contract
          </Button>
        )}
      </ListPageHeader>

      <ListFilterPanel hasActiveFilters={hasActiveFilters} onClearFilters={handleClearFilters}>
        <div>
          <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Vehicle</label>
          <select
            name="vehicle_id"
            value={filters.vehicle_id}
            onChange={handleFilterChange}
            className="w-full px-3 py-1.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          >
            <option value="">All Vehicles</option>
            {vehicles.map((v) => (
              <option key={v.id} value={v.id}>
                {v.vehicle_number} — {v.vehicle_name}
              </option>
            ))}
          </select>
        </div>
      </ListFilterPanel>

      {!(loading && currentPage === 1) && aggregate.contracts > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mb-4">
          {[
            { label: "Contracts", value: aggregate.contracts },
            { label: "Shipments", value: aggregate.shipments },
            { label: "Freight", value: `₹${fmt(aggregate.freight)}` },
            { label: "Contract Value", value: `₹${fmt(aggregate.contract_value)}` },
            { label: "Paid", value: `₹${fmt(aggregate.paid_amount)}` },
            { label: "Balance", value: `₹${fmt(aggregate.balance_amount)}` },
            { label: "Trip P&L", value: `₹${fmt(aggregate.trip_pl)}` },
          ].map((item) => (
            <div key={item.label} className="bg-white rounded-xl shadow-sm border border-gray-100 p-3">
              <div className="text-xs text-gray-500 font-medium">{item.label}</div>
              <div className="text-base font-bold text-gray-900 mt-1">{item.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* Table / Cards */}
      <div className="w-full min-w-0">
        {loading && currentPage === 1 ? (
          <div className="flex items-center justify-center py-20">
            <Loader fullScreen={false} size="default" message="Loading contracts..." />
          </div>
        ) : contracts.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-100 text-center py-16 shadow-sm">
            <p className="text-gray-600 text-lg font-semibold">No contracts found</p>
            {canEdit() && (
              <Button variant="primary" icon={Plus} onClick={() => navigate('/on-contracts/add')} className="mt-4">
                Add Contract
              </Button>
            )}
          </div>
        ) : (
          <>
            {/* Desktop table with horizontal scroll */}
            <div className="hidden xl:block bg-white rounded-xl shadow-sm border border-gray-100 w-full min-w-0">
              <div className="w-full overflow-x-auto overscroll-x-contain">
                <table className="w-full min-w-[1280px] border-collapse">
                  <thead className="bg-gray-50">
                    <tr className="border-b border-gray-200">
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">Sr</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">Contract ID</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap min-w-[120px]">Vehicle</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">Period</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">{t('sidebar.masterLoadingSheet')}</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">{t('pages.shipment.title')}</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">Freight (₹)</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">Contract Val (₹)</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">Paid (₹)</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">Balance</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">Trip P&L</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-700 uppercase tracking-wider whitespace-nowrap">{t('common.actions')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {contracts.map((c, idx) => (
                      <tr key={c.id} className={`hover:bg-gray-50 transition-colors ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}>
                        <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{idx + 1}</td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <button
                            type="button"
                            onClick={() => navigate(`/on-contracts/view/${c.contract_id}`)}
                            className={listIdLinkClass}
                          >
                            {c.contract_id}
                          </button>
                        </td>
                        <td className="px-4 py-3 min-w-[120px]">
                          <div className="text-sm font-medium text-gray-900">{c.vehicle_number}</div>
                          <div className="text-xs text-gray-500 truncate max-w-[140px]">{c.vehicle_name}</div>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                          <div>{c.start_date}</div>
                          <div className="text-gray-400 text-xs">→ {c.end_date}</div>
                        </td>
                        <td className="px-4 py-3 text-center text-sm text-gray-600 whitespace-nowrap">{c.ml_sheet_ids?.length || 0}</td>
                        <td className="px-4 py-3 text-center text-sm text-gray-600 whitespace-nowrap">{c.total_shipments}</td>
                        <td className="px-4 py-3 text-right text-sm font-medium text-gray-900 whitespace-nowrap">₹{fmt(c.total_revenue)}</td>
                        <td className="px-4 py-3 text-right text-sm text-gray-600 whitespace-nowrap">₹{fmt(c.contract_value)}</td>
                        <td className="px-4 py-3 text-right text-sm font-medium text-gray-900 whitespace-nowrap">₹{fmt(c.paid_amount)}</td>
                        <td className="px-4 py-3 text-center whitespace-nowrap">
                          <BalanceBadge value={c.balance_amount} />
                        </td>
                        <td className="px-4 py-3 text-center whitespace-nowrap">
                          <PLBadge value={c.trip_pl} />
                        </td>
                        <td className="px-4 py-3 text-center whitespace-nowrap">
                          <RowActionsMenu
                            className="relative menu-container"
                            isOpen={openMenuId === c.contract_id}
                            onToggle={() => setOpenMenuId(openMenuId === c.contract_id ? null : c.contract_id)}
                            onClose={() => setOpenMenuId(null)}
                            onView={() => navigate(`/on-contracts/view/${c.contract_id}`)}
                            onEdit={canEdit() ? () => navigate(`/on-contracts/edit/${c.contract_id}`) : undefined}
                            onDelete={canDelete() ? () => handleDelete(c.contract_id) : undefined}
                            extraItems={[
                              {
                                label: exportingId === c.contract_id ? 'Exporting...' : 'Excel',
                                icon: '📊',
                                onClick: () => handleExport(c.contract_id),
                              },
                            ]}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="px-5 py-3 bg-gray-50 border-t border-gray-200 text-sm text-gray-700 rounded-b-xl">
                Showing {contracts.length} of {aggregate.contracts} contract{aggregate.contracts !== 1 ? 's' : ''}
              </div>
            </div>

            {/* Tablet & Mobile cards */}
            <div className="xl:hidden space-y-3">
              {contracts.map((c) => (
                <div key={c.id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 border-l-4 border-l-blue-500">
                  <div className="flex justify-between items-start mb-3 gap-2">
                    <div className="min-w-0">
                      <button
                        type="button"
                        onClick={() => navigate(`/on-contracts/view/${c.contract_id}`)}
                        className={listIdLinkMobileClass}
                      >
                        {c.contract_id}
                      </button>
                      <div className="text-sm text-gray-600 mt-0.5 break-words">{c.vehicle_number} — {c.vehicle_name}</div>
                      <div className="text-xs text-gray-400 mt-0.5">{c.start_date} → {c.end_date}</div>
                    </div>
                    <div className="shrink-0 flex items-center gap-2">
                      <PLBadge value={c.trip_pl} />
                      <RowActionsMenu
                        className="relative menu-container"
                        isOpen={openMenuId === c.contract_id}
                        onToggle={() => setOpenMenuId(openMenuId === c.contract_id ? null : c.contract_id)}
                        onClose={() => setOpenMenuId(null)}
                        onView={() => navigate(`/on-contracts/view/${c.contract_id}`)}
                        onEdit={canEdit() ? () => navigate(`/on-contracts/edit/${c.contract_id}`) : undefined}
                        onDelete={canDelete() ? () => handleDelete(c.contract_id) : undefined}
                        extraItems={[
                          {
                            label: exportingId === c.contract_id ? 'Exporting...' : 'Excel',
                            icon: '📊',
                            onClick: () => handleExport(c.contract_id),
                          },
                        ]}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-xs sm:text-sm">
                    <div>
                      <p className="text-xs text-gray-500 font-medium">{t('sidebar.masterLoadingSheet')}</p>
                      <p className="font-semibold text-gray-800">{c.ml_sheet_ids?.length || 0}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 font-medium">{t('pages.shipment.title')}</p>
                      <p className="font-semibold text-gray-800">{c.total_shipments}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 font-medium">Freight</p>
                      <p className="font-semibold text-gray-800">₹{fmt(c.total_revenue)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 font-medium">Contract Val</p>
                      <p className="font-semibold text-gray-800">₹{fmt(c.contract_value)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 font-medium">Paid</p>
                      <p className="font-semibold text-green-600">₹{fmt(c.paid_amount)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 font-medium">Balance</p>
                      <BalanceBadge value={c.balance_amount} />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <ListPagination
              currentPage={currentPage}
              totalPages={totalPages}
              loading={loading}
              onNext={() => setCurrentPage((page) => page + 1)}
            />
          </>
        )}
      </div>
    </DashboardLayout>
  )
}

export default OnContractList
